import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { useLocation, useSearch } from "wouter";

/** Durée maximale de la reprise d'une position (le contenu arrive en différé). */
const IMAGES_MAX = 90; // environ 1,5 s à 60 images par seconde

/**
 * Défilement de la zone de contenu d'un écran à l'autre (tournage du
 * 10/10/2026) : la zone `<main>` de l'application persiste d'un écran à
 * l'autre et gardait sa position, si bien qu'un nouvel écran s'ouvrait à
 * mi-page, sans son titre. `window.scrollTo` n'y fait rien : c'est `<main>`
 * qui défile.
 *
 * Règle : chaque changement d'écran (chemin ou paramètres) remonte en haut,
 * sauf un retour arrière ou avant du navigateur, qui retrouve la position
 * laissée sur cet écran. Le contenu arrivant après coup, la position est
 * reprise image par image jusqu'à être atteinte (au plus 1,5 s), et
 * abandonnée dès que la personne fait défiler elle-même.
 */
export function useDefilementEcran(conteneur: RefObject<HTMLElement | null>) {
  const [chemin] = useLocation();
  const recherche = useSearch();
  const cle = recherche ? `${chemin}?${recherche}` : chemin;

  const positions = useRef(new Map<string, number>());
  const cleCourante = useRef(cle);
  const retour = useRef(false);

  useEffect(() => {
    const el = conteneur.current;
    const surDefilement = () => {
      if (el) positions.current.set(cleCourante.current, el.scrollTop);
    };
    const surRetour = () => {
      retour.current = true;
    };
    el?.addEventListener("scroll", surDefilement, { passive: true });
    window.addEventListener("popstate", surRetour);
    return () => {
      el?.removeEventListener("scroll", surDefilement);
      window.removeEventListener("popstate", surRetour);
    };
  }, [conteneur]);

  useLayoutEffect(() => {
    if (cleCourante.current === cle) return;
    cleCourante.current = cle;
    const estRetour = retour.current;
    retour.current = false;
    const el = conteneur.current;
    if (!el) return;
    const cible = estRetour ? positions.current.get(cle) ?? 0 : 0;
    el.scrollTop = cible;
    if (cible <= 0 || typeof requestAnimationFrame !== "function") return;

    let images = 0;
    let id = 0;
    let abandon = false;
    const arreter = () => {
      abandon = true;
    };
    const reprendre = () => {
      if (abandon || Math.abs(el.scrollTop - cible) < 2 || images++ >= IMAGES_MAX) return;
      el.scrollTop = cible;
      id = requestAnimationFrame(reprendre);
    };
    el.addEventListener("wheel", arreter, { passive: true });
    el.addEventListener("touchmove", arreter, { passive: true });
    el.addEventListener("keydown", arreter);
    id = requestAnimationFrame(reprendre);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener("wheel", arreter);
      el.removeEventListener("touchmove", arreter);
      el.removeEventListener("keydown", arreter);
    };
  }, [cle, conteneur]);
}
